import React, { useEffect, useMemo, useState } from "react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { logActivity } from "../../utils/activity";
import { usePendingReports } from "../../context/PendingReports";
import { useWebSocket } from "../../context/WebSocketContext";
import SearchableSelect from "../SearchableSelect/SearchableSelect";
import "./ReportFlow.css";
import { formatTime } from "../../utils/format";

const GAMES_CLOUD_BASE = (process.env.REACT_APP_GAMES_CLOUD_URL || "")
    .replace(/^ws/, "http")
    .replace(/ws$/, "");

const GROUP_TYPES = ["Familie", "Venner", "Kolleger", "Par", "Turister", "Firma arrangement", "Fødselsdag", "Polter Abend", "Entusiaster"];
const HAPPINESS = [
    { value: "1", label: "1 Meget utilfreds" },
    { value: "2", label: "2 Noget gik galt" },
    { value: "3", label: "3 Fin oplevelse" },
    { value: "4", label: "4 Glade kunder" },
    { value: "5", label: "5 Super glade" },
    { value: "Ved ikke", label: "Ved ikke" },
];

function formatSlotTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return formatTime(d);
}

// parsePlayerRange turns a booked player-count into [min, max] numbers. Accepts a single number
// ("6" → [6, 6]) or a range ("2-4" → [2, 4]). Unparseable/empty input yields [NaN, NaN], which
// disables the discrepancy check rather than firing it spuriously.
function parsePlayerRange(players) {
    const parts = String(players || "").split("-").map(p => Number(p.trim()));
    if (parts.length === 1 && !isNaN(parts[0])) return [parts[0], parts[0]];
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) return [parts[0], parts[1]];
    return [NaN, NaN];
}

const emptyForm = {
    actual_players: "", paid_extra: "", discrepancy_reason: "",
    group_type: "", happiness: "",
    gm_onsite: "", gm_remote: "", host_start: "", host_end: "", gm_remark: "",
    completed: "", minutes: "", seconds: "", team_name: "", entered_room: "", entered_room_reason: "",
    tidskort_brochure: false, solgt_drikkevarer: "", solgt_merchandise: "",
    solgt_ny_booking: false, billede_med_device: false, golden_ticket: false,
};

// ReportFlow is the "afslut spil" experience: the list of today's un-reported games and the
// per-game questionnaire. It has no Topbar of its own so it can live both on the EndGame page and
// embedded in the HUD's right column. `onClose` (embedded) shows a "back to game" control on the
// list. When rendered inside a room's WebSocketProvider it reads the live timer to prefill the
// completion-time field; on the standalone page useWebSocket() is undefined and it stays blank.
export default function ReportFlow({ onClose }) {
    const { games: visible, loading, error, refresh, removeGame, gmNames, ownName } = usePendingReports();
    const ws = useWebSocket();
    // Live timer, split into whole minutes + seconds so the completion time carries seconds (the
    // "bedste tider" screen ranks on it; whole minutes tie far too often).
    const timerSeconds = ws && ws.elapsedTime != null ? Math.max(0, Math.round(ws.elapsedTime)) : null;
    const timerMinutes = timerSeconds != null ? Math.floor(timerSeconds / 60) : null;

    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);
    const [submitMsg, setSubmitMsg] = useState(null);
    const [suggesting, setSuggesting] = useState(false);

    // "Foreslå holdnavn": a generated name from games-cloud for a group that cannot think of one.
    // Clicking again fetches a fresh one. A blank field is fine too — the server generates the
    // same kind of name on submit — but this way the GM sees what goes on the screen.
    async function suggestTeamName() {
        setSuggesting(true);
        try {
            const res = await fetchWithAuth(`${GAMES_CLOUD_BASE}report/team-name`);
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.team_name) updateForm("team_name", data.team_name);
        } catch (e) {
            // Best effort — the field simply stays as it is.
        } finally {
            setSuggesting(false);
        }
    }

    useEffect(() => { refresh(); }, [refresh]);

    function selectGame(g) {
        setSelected(g);
        setSubmitMsg(null);
        setForm({
            ...emptyForm,
            minutes: timerMinutes != null ? String(timerMinutes) : "",
            seconds: timerSeconds != null ? String(timerSeconds % 60) : "",
        });
    }

    function updateForm(field, value) {
        setForm(prev => ({ ...prev, [field]: value }));
    }

    // Player-count discrepancy vs the booking. Booked count can be a single number ("6") or a range
    // ("2-4"), so parse it into [min, max]: fewer than min or more than max counts as a discrepancy.
    const [bookedMin, bookedMax] = parsePlayerRange(selected ? selected.players : "");
    const actual = Number(form.actual_players);
    const hasActual = form.actual_players !== "" && !isNaN(actual);
    const moreThanBooked = hasActual && !isNaN(bookedMax) && actual > bookedMax;
    const fewerThanBooked = hasActual && !isNaN(bookedMin) && actual < bookedMin;
    // A low satisfaction rating (1-2) requires a GM remark explaining what happened.
    const remarkRequired = form.happiness === "1" || form.happiness === "2";

    // GM onsite and GM remote are two different people by definition, so each picker hides the
    // other's choice. A game one GM ran alone is onsite only — leave remote blank.
    const onsiteOptions = useMemo(() => gmNames.filter(n => n !== form.gm_remote), [gmNames, form.gm_remote]);
    const remoteOptions = useMemo(() => gmNames.filter(n => n !== form.gm_onsite), [gmNames, form.gm_onsite]);

    async function handleSubmit(e) {
        e.preventDefault();
        setSubmitMsg(null);

        if (!form.actual_players || !form.group_type || !form.happiness || !form.completed || !form.entered_room || !form.minutes || !form.host_start.trim() || !form.host_end.trim() || !form.gm_onsite.trim()) {
            setSubmitMsg({ ok: false, text: "Udfyld de påkrævede felter (markeret med *)." });
            return;
        }
        if (!isFinite(Number(form.minutes)) || Number(form.minutes) < 0) {
            setSubmitMsg({ ok: false, text: "\"Tid\" skal være et tal (minutter)." });
            return;
        }
        if (form.seconds !== "" && (!Number.isInteger(Number(form.seconds)) || Number(form.seconds) < 0 || Number(form.seconds) > 59)) {
            setSubmitMsg({ ok: false, text: "Sekunder skal være et helt tal mellem 0 og 59." });
            return;
        }
        if (moreThanBooked && !form.paid_extra) {
            setSubmitMsg({ ok: false, text: "Angiv om kunden har betalt ekstra for de ekstra spillere." });
            return;
        }
        if (moreThanBooked && form.paid_extra === "Nej" && !form.discrepancy_reason) {
            setSubmitMsg({ ok: false, text: "Skriv hvorfor kunden ikke har betalt ekstra." });
            return;
        }
        if (form.gm_remote.trim() && form.gm_remote.trim() === form.gm_onsite.trim()) {
            setSubmitMsg({ ok: false, text: "GM onsite og GM remote skal være to forskellige personer." });
            return;
        }
        if (form.entered_room === "Ja" && !form.entered_room_reason.trim()) {
            setSubmitMsg({ ok: false, text: "Skriv hvorfor I gik ind i rummet." });
            return;
        }
        if (remarkRequired && !form.gm_remark.trim()) {
            setSubmitMsg({ ok: false, text: "Ved lav tilfredshed (1-2) skal GM bemærkning udfyldes." });
            return;
        }

        setSubmitting(true);
        try {
            const jn = (b) => (b ? "Ja" : "Nej");
            const body = {
                start_time: selected.start_time,
                room_name: selected.room_name || "",
                calendar_id: selected.calendar_id || "",
                product_id: selected.product_id || "",
                first_name: selected.customer_name || "",
                email: (selected.attendee_emails && selected.attendee_emails[0]) || "",
                booked_players: selected.players || "",
                event_id: selected.event_id,
                location: selected.location || "",
                actual_players: form.actual_players,
                paid_extra: moreThanBooked ? form.paid_extra : "",
                discrepancy_reason: moreThanBooked && form.paid_extra === "Nej" ? form.discrepancy_reason : "",
                group_type: form.group_type,
                happiness: form.happiness,
                gm_onsite: form.gm_onsite,
                gm_remote: form.gm_remote,
                host_start: form.host_start,
                host_end: form.host_end,
                gm_remark: form.gm_remark,
                completed: form.completed,
                minutes: form.minutes,
                seconds: form.seconds === "" ? "0" : form.seconds,
                team_name: form.team_name.trim(),
                entered_room: form.entered_room,
                entered_room_reason: form.entered_room === "Ja" ? form.entered_room_reason.trim() : "",
                tidskort_brochure: jn(form.tidskort_brochure),
                solgt_drikkevarer: form.solgt_drikkevarer,
                solgt_merchandise: form.solgt_merchandise,
                solgt_ny_booking: jn(form.solgt_ny_booking),
                billede_med_device: jn(form.billede_med_device),
                golden_ticket: jn(form.golden_ticket),
            };
            const res = await fetchWithAuth(`${GAMES_CLOUD_BASE}report`, {
                method: "POST",
                body: JSON.stringify(body),
            });
            const data = await res.json().catch(() => ({}));
            if (data.result === "success") {
                logActivity("end_game", `${selected.room_name} — ${form.happiness}/5`);
                removeGame(selected.event_id);
                setSelected(null);
                setSubmitMsg({ ok: true, text: "Spillet er afsluttet og gemt." });
            } else {
                setSubmitMsg({ ok: false, text: data.message || "Kunne ikke gemme rapporten." });
            }
        } catch (err) {
            const msg = String(err.message).includes("409")
                ? "Spillet er allerede afsluttet."
                : "Kunne ikke gemme rapporten. Prøv igen.";
            setSubmitMsg({ ok: false, text: msg });
        } finally {
            setSubmitting(false);
        }
    }

    if (selected) {
        return (
            <div className="endgame-panel">
                <button type="button" className="endgame-back" onClick={() => setSelected(null)}>← Listen</button>
                <h2>{selected.room_name}</h2>

                <div className="endgame-booking">
                    <div className="endgame-field">
                        <span className="endgame-label">Gæst</span>
                        <span className="endgame-value">{selected.customer_name || "—"}</span>
                    </div>
                    <div className="endgame-field">
                        <span className="endgame-label">Telefon</span>
                        <span className="endgame-value">{selected.phone || "—"}</span>
                    </div>
                    <div className="endgame-field">
                        <span className="endgame-label">Bookede spillere</span>
                        <span className="endgame-value">{selected.players || "—"}</span>
                    </div>
                    <div className="endgame-field">
                        <span className="endgame-label">Tidspunkt</span>
                        <span className="endgame-value">{formatSlotTime(selected.start_time)}</span>
                    </div>
                </div>

                {submitMsg && !submitMsg.ok && <div className="endgame-error">{submitMsg.text}</div>}

                <form className="endgame-form" onSubmit={handleSubmit}>
                    <div className="endgame-form-grid">
                        <label>
                            Antal spillere *
                            <input type="number" min="0" value={form.actual_players} onChange={e => updateForm("actual_players", e.target.value)} />
                        </label>
                        <label>
                            Gruppe *
                            <select value={form.group_type} onChange={e => updateForm("group_type", e.target.value)}>
                                <option value="" disabled>-- Vælg --</option>
                                {GROUP_TYPES.map(g => <option key={g}>{g}</option>)}
                            </select>
                        </label>
                        <label>
                            Kundetilfredshed *
                            <select value={form.happiness} onChange={e => updateForm("happiness", e.target.value)}>
                                <option value="" disabled>-- Vælg --</option>
                                {HAPPINESS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                            </select>
                        </label>
                        <label>
                            Gennemført *
                            <select value={form.completed} onChange={e => updateForm("completed", e.target.value)}>
                                <option value="" disabled>-- Vælg --</option>
                                <option>Ja</option>
                                <option>Nej</option>
                            </select>
                        </label>
                        <label>
                            Gik ind i rummet *
                            <select value={form.entered_room} onChange={e => updateForm("entered_room", e.target.value)}>
                                <option value="" disabled>-- Vælg --</option>
                                <option>Nej</option>
                                <option>Ja</option>
                            </select>
                            <span className="report-hint">Blev I nødt til at gå ind i rummet under spillet?</span>
                        </label>
                        <label>
                            Tid *
                            <span className="report-time">
                                <input type="text" inputMode="numeric" placeholder="52" value={form.minutes} onChange={e => updateForm("minutes", e.target.value)} />
                                <span className="report-time__unit">min</span>
                                <input type="text" inputMode="numeric" placeholder="30" value={form.seconds} onChange={e => updateForm("seconds", e.target.value)} />
                                <span className="report-time__unit">sek</span>
                            </span>
                        </label>
                        <label>
                            Holdnavn
                            <span className="report-teamname">
                                <input type="text" maxLength={40} placeholder="Vises på bedste-tider-skærmen" value={form.team_name} onChange={e => updateForm("team_name", e.target.value)} />
                                <button type="button" className="report-suggest" onClick={suggestTeamName} disabled={suggesting}>
                                    {form.team_name.trim() ? "Nyt forslag" : "Foreslå holdnavn"}
                                </button>
                            </span>
                            <span className="report-hint">
                                {form.completed === "Ja"
                                    ? "Holdet kommer på bedste-tider-skærmen med det samme. Tomt felt får et automatisk holdnavn."
                                    : "Kun gennemførte spil kommer på bedste-tider-skærmen. Tomt felt får et automatisk holdnavn."}
                            </span>
                        </label>
                        <label>
                            GM onsite *
                            <SearchableSelect value={form.gm_onsite} onChange={v => updateForm("gm_onsite", v)}
                                options={onsiteOptions} ownName={ownName} placeholder="-- Vælg --" />
                        </label>
                        <label>
                            GM remote
                            <SearchableSelect value={form.gm_remote} onChange={v => updateForm("gm_remote", v)}
                                options={remoteOptions} ownName={ownName} placeholder="-- Vælg --" />
                        </label>
                        <label>
                            Vært ved start *
                            <SearchableSelect value={form.host_start} onChange={v => updateForm("host_start", v)}
                                options={gmNames} ownName={ownName} placeholder="-- Vælg --" />
                        </label>
                        <label>
                            Vært ved afslutning *
                            <SearchableSelect value={form.host_end} onChange={v => updateForm("host_end", v)}
                                options={gmNames} ownName={ownName} placeholder="-- Vælg --" />
                        </label>
                        <label>
                            Solgt drikkevarer (antal)
                            <input type="number" min="0" value={form.solgt_drikkevarer} onChange={e => updateForm("solgt_drikkevarer", e.target.value)} />
                        </label>
                        <label>
                            Solgt merchandise/spil (kr)
                            <input type="number" min="0" value={form.solgt_merchandise} onChange={e => updateForm("solgt_merchandise", e.target.value)} />
                        </label>
                    </div>

                    {/* Player-count discrepancy vs the booking. */}
                    {moreThanBooked && (
                        <div className="endgame-discrepancy">
                            <strong>Flere spillere end booket ({form.actual_players} vs {selected.players}).</strong>
                            <label>
                                Har kunden betalt ekstra? *
                                <select value={form.paid_extra} onChange={e => updateForm("paid_extra", e.target.value)}>
                                    <option value="" disabled>-- Vælg --</option>
                                    <option>Ja</option>
                                    <option>Nej</option>
                                </select>
                            </label>
                            {form.paid_extra === "Nej" && (
                                <label>
                                    Hvorfor ikke? *
                                    <input type="text" value={form.discrepancy_reason} onChange={e => updateForm("discrepancy_reason", e.target.value)} />
                                </label>
                            )}
                        </div>
                    )}
                    {fewerThanBooked && (
                        <div className="endgame-discrepancy">
                            <strong>Færre spillere end booket ({form.actual_players} vs {selected.players}).</strong>
                            <span> Giv kunden en golden ticket (sæt fluebenet nedenfor).</span>
                        </div>
                    )}

                    {/* Why the GM went in — shown in the morning "daglig drift" card next to the game. */}
                    {form.entered_room === "Ja" && (
                        <label className="endgame-remark">
                            Hvorfor gik I ind i rummet? *
                            <textarea rows="2" placeholder="Fx: skrivemaskinen sad fast, holdet var låst fast på kodelås 3" value={form.entered_room_reason} onChange={e => updateForm("entered_room_reason", e.target.value)} />
                            <span className="report-hint">Vises i den daglige driftsoversigt sammen med spillet.</span>
                        </label>
                    )}

                    <label className="endgame-remark">
                        GM bemærkning{remarkRequired ? " * (påkrævet ved lav tilfredshed)" : ""}
                        <textarea rows="2" value={form.gm_remark} onChange={e => updateForm("gm_remark", e.target.value)} />
                    </label>

                    <div className="endgame-checks">
                        <label><input type="checkbox" checked={form.tidskort_brochure} onChange={e => updateForm("tidskort_brochure", e.target.checked)} /> Tidskort brochure</label>
                        <label><input type="checkbox" checked={form.solgt_ny_booking} onChange={e => updateForm("solgt_ny_booking", e.target.checked)} /> Solgt ny booking</label>
                        <label><input type="checkbox" checked={form.billede_med_device} onChange={e => updateForm("billede_med_device", e.target.checked)} /> Billede med kundens device</label>
                        <label><input type="checkbox" checked={form.golden_ticket} onChange={e => updateForm("golden_ticket", e.target.checked)} /> Golden Ticket udleveret</label>
                    </div>

                    <button type="submit" className="endgame-submit" disabled={submitting}>
                        {submitting ? "Gemmer…" : "Afslut spil"}
                    </button>
                </form>
            </div>
        );
    }

    return (
        <div className="endgame-list">
            {onClose && (
                <button type="button" className="endgame-back" onClick={onClose}>← Tilbage til spillet</button>
            )}
            <h2>Spil der mangler at blive afsluttet</h2>

            {submitMsg && (
                <div className={submitMsg.ok ? "endgame-success" : "endgame-error"}>{submitMsg.text}</div>
            )}
            {loading && visible.length === 0 && <div className="endgame-muted">Henter spil…</div>}
            {error && <div className="endgame-error">Kunne ikke hente spil. Prøv igen.</div>}
            {!loading && !error && visible.length === 0 && (
                <div className="endgame-muted">Ingen spil mangler at blive afsluttet lige nu. 🎉</div>
            )}

            {[...visible]
                .sort((a, b) => {
                    const room = (a.room_name || "").localeCompare(b.room_name || "", "da");
                    if (room !== 0) return room;
                    return (a.start_time || "").localeCompare(b.start_time || "");
                })
                .map(g => (
                    <button
                        key={`${g.calendar_id}:${g.event_id}`}
                        className="endgame-game-row"
                        onClick={() => selectGame(g)}
                    >
                        <span className="endgame-game-guest">
                            {g.room_name || "Ukendt rum"} · {formatSlotTime(g.start_time)}
                        </span>
                        <span className="endgame-game-meta">
                            {g.customer_name || "Ukendt gæst"}
                            {g.players ? ` · ${g.players} spillere` : ""}
                        </span>
                    </button>
                ))}
        </div>
    );
}
